const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getToken = () => localStorage.getItem('token') || localStorage.getItem('vincentrix_token') || '';
const getDealerId = () => {
  for (const key of ['dealership', 'dealer', 'vincentrix_dealer']) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const value = JSON.parse(raw);
      if (value?.id) return value.id;
    } catch {}
  }
  return '';
};

const first = (...values) => values.find(value => value !== undefined && value !== null && String(value).trim() !== '') ?? '';
const digits = value => String(value ?? '').replace(/[^0-9]/g, '');
const cleanMoney = value => {
  const raw = String(value ?? '').replace(/[^0-9.-]/g, '');
  const n = Number(raw);
  return Number.isFinite(n) && raw ? n.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '';
};
const customerName = c => first(c?.name, c?.customerName, [c?.firstName, c?.lastName].filter(Boolean).join(' '));
const today = () => new Date().toLocaleDateString('en-US', { month:'2-digit', day:'2-digit', year:'numeric' });

let customerCache = [];
let customerCacheAt = 0;

const authHeaders = () => {
  const token = getToken();
  const dealerId = getDealerId();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(dealerId ? { 'X-Dealership-Id': dealerId, 'X-Dealer-Id': dealerId } : {})
  };
};

const loadCustomers = async () => {
  if (customerCache.length && Date.now() - customerCacheAt < 15000) return customerCache;
  const response = await fetch(`${API_URL}/api/customers`, { headers: authHeaders() });
  if (!response.ok) return customerCache;
  const data = await response.json().catch(() => ({}));
  customerCache = Array.isArray(data.customers) ? data.customers : [];
  customerCacheAt = Date.now();
  return customerCache;
};

const activePrintCustomer = async () => {
  const modal = document.querySelector('.vc-pp-modal');
  const drawer = document.querySelector('.vc-opportunity-drawer');
  const text = `${modal?.innerText || ''}\n${drawer?.innerText || ''}`;
  const customers = await loadCustomers();
  const vin = text.match(/\b([A-HJ-NPR-Z0-9]{17})\b/i)?.[1]?.toUpperCase();
  if (vin) {
    const match = customers.find(c => String(first(c.vin,c.vehicleVin,c.vehicleVIN) || '').toUpperCase() === vin);
    if (match) return match;
  }
  return customers.find(c => {
    const name = customerName(c);
    return name && text.toLowerCase().includes(String(name).toLowerCase());
  }) || null;
};

const cityStateZip = c => {
  const city = first(c?.city, c?.customerCity, c?.addressCity);
  const state = first(c?.state, c?.customerState, c?.addressState);
  const zip = first(c?.zip, c?.zipCode, c?.postalCode, c?.customerZip, c?.addressZip);
  return [city, state, zip].filter(Boolean).join(' ');
};

const canonicalValues = c => {
  const year = first(c?.vehicleYear, c?.year);
  const make = first(c?.vehicleMake, c?.make);
  const model = first(c?.vehicleModel, c?.model);
  const vin = first(c?.vin, c?.vehicleVin, c?.vehicleVIN);
  const name = customerName(c);
  return {
    date: today(), year, make, model, vin,
    license_plate: first(c?.licensePlate, c?.plate, c?.plateNumber, c?.tag),
    sales_price: cleanMoney(first(c?.purchasePrice, c?.acquisitionPrice, c?.agreedPrice)),
    customer_name: name,
    mileage: digits(first(c?.mileage, c?.vehicleMileage, c?.odometer, c?.miles)),
    customer_address: first(c?.address, c?.streetAddress, c?.address1, c?.customerAddress, c?.addressLine1),
    customer_city_state_zip: cityStateZip(c),
    payoff: cleanMoney(first(c?.payoff, c?.payoffAmount, c?.tradePayoff, c?.twentyDayPayoff))
  };
};

const normalizeField = name => String(name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const fieldValueForName = (name, values) => {
  const n = normalizeField(name).replace(/\d+$/,'');
  const aliases = [
    [['customername','sellername','transferorname','ownername','fullname','name'], 'customer_name'],
    [['vin','vehicleidentificationnumber','vehiclevin'], 'vin'],
    [['mileage','miles','odometer','odometerreading'], 'mileage'],
    [['year','vehicleyear'], 'year'],
    [['make','vehiclemake'], 'make'],
    [['model','vehiclemodel'], 'model'],
    [['date','transactiondate','purchasedate'], 'date'],
    [['customeraddress','selleraddress','transferoraddress','streetaddress','address'], 'customer_address'],
    [['customercitystatezip','citystatezip','sellercitystatezip','transferorcitystatezip'], 'customer_city_state_zip'],
    [['licenseplate','plate','platenumber','tag'], 'license_plate'],
    [['salesprice','purchaseprice','acquisitionprice','agreedprice','offeramount','price'], 'sales_price'],
    [['payoff','payoffamount','lienpayoff'], 'payoff']
  ];
  for (const [names,key] of aliases) if (names.includes(n)) return values[key];
  return undefined;
};

const fillDocument = async pdfDoc => {
  const customer = await activePrintCustomer();
  if (!customer) {
    console.warn('VinCentrix Print Pack: no active customer matched; auto-fill skipped.');
    return pdfDoc;
  }
  let form;
  try { form = pdfDoc.getForm(); } catch { return pdfDoc; }
  const values = canonicalValues(customer);
  let filled = 0;
  let fields = [];
  try { fields = form.getFields(); } catch {}
  for (const field of fields) {
    let name = '';
    try { name = field.getName(); } catch { continue; }
    const value = fieldValueForName(name, values);
    if (value === undefined || value === '') continue;
    try {
      if (typeof field.setText === 'function') {
        field.setText(String(value));
        filled += 1;
      }
    } catch (error) { console.warn(`Print Pack field ${name} could not be filled:`, error); }
  }
  if (filled) {
    try { form.updateFieldAppearances(); } catch {}
    try { form.flatten(); } catch (error) { console.warn('Print Pack field flattening failed:', error); }
    console.info(`VinCentrix Print Pack: auto-filled and flattened ${filled} field(s) for ${customerName(customer)}.`);
  } else {
    console.warn('VinCentrix Print Pack: PDF has no recognized fillable fields.');
  }
  return pdfDoc;
};

const patchPdfLib = () => {
  const PDFDocument = window.PDFLib?.PDFDocument;
  if (!PDFDocument || PDFDocument.__vcAutoFillPatched) return false;
  const originalLoad = PDFDocument.load.bind(PDFDocument);
  PDFDocument.load = async (...args) => {
    const doc = await originalLoad(...args);
    try { await fillDocument(doc); }
    catch (error) { console.warn('Print Pack auto-fill skipped:', error); }
    return doc;
  };
  PDFDocument.__vcAutoFillPatched = true;
  return true;
};

const originalAppendChild = document.head.appendChild.bind(document.head);
document.head.appendChild = function(node) {
  if (node?.id === 'vc-pdf-lib') node.addEventListener('load', patchPdfLib);
  return originalAppendChild(node);
};

patchPdfLib();
export {};
